import {
  KVAerospaceAnnotation,
  KVG1N6Annotation,
  KVNavyAnnotation,
  KVRooftopAnnotation,
  WorldPosition,
} from 'types'

/** Tag metadata */
export interface KVTagMetadata {
  value: string
  derivedTag?: boolean
}

/** Geometry payloads */
export class KVPointGeometry {
  kind = 'point' as const
  position?: WorldPosition
  normal?: WorldPosition

  constructor(position?: WorldPosition, normal?: WorldPosition) {
    this.position = position
    this.normal = normal
  }

  toJSON() {
    return {
      kind: this.kind,
      position: this.position,
      normal: this.normal,
    }
  }
}

export class KVPolygonGeometry {
  kind = 'polygon' as const
  points: WorldPosition[]
  normal?: WorldPosition

  constructor(points: WorldPosition[], normal?: WorldPosition) {
    this.points = points
    this.normal = normal
  }

  toJSON() {
    return {
      kind: this.kind,
      points: this.points,
      normal: this.normal,
    }
  }
}

export type KVGeometry = KVPointGeometry | KVPolygonGeometry

export class KVAnnotation {
  id: string
  label: string
  notes: string
  severity: string
  type: string
  visible: boolean
  imageNames: string[]
  userName: string
  userEmail: string
  createdAt: string
  historical: boolean // Historical field is only set locally since it changes based on which model pulls the annotation

  // Centered normal/position for zooming and photo selection
  centeredNormal?: WorldPosition
  centeredPosition?: WorldPosition

  // Main geometry data - either point or polygon
  geometry?: KVGeometry

  // Segmentation flag for polygon annotations (surface areas)
  isSegmentationSurfaceArea?: boolean

  // Area and perimeter for polygon annotations (surface areas)
  area?: number
  perimeter?: number

  // Bounding box dimensions for segmentation surface areas
  boundingBoxLength?: number
  boundingBoxWidth?: number

  // Custom-optional forms
  navyAnnotation?: KVNavyAnnotation
  rooftopAnnotation?: KVRooftopAnnotation
  g1n6Annotation?: KVG1N6Annotation
  aerospaceAnnotation?: KVAerospaceAnnotation

  // For dataset generation; did a human OK this annotation?
  humanValidated?: boolean

  // Optional group identifier for filtering/organizing annotations
  group?: string

  tags: Record<string, KVTagMetadata>

  constructor(id: string) {
    this.id = id
    this.label = ''
    this.notes = ''
    this.severity = '3'
    this.type = 'Misc.'
    this.visible = true
    this.imageNames = []
    this.userName = ''
    this.userEmail = ''
    this.createdAt = new Date().toISOString()
    this.historical = false
    this.tags = {}
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  static fromJSON(json: any): KVAnnotation {
    const annotation = new KVAnnotation(json.annotationId)
    if (json.annotationLabel) annotation.label = json.annotationLabel
    if (json.annotationNotes) annotation.notes = json.annotationNotes
    if (json.annotationSeverity) annotation.severity = json.annotationSeverity
    if (json.annotationType) annotation.type = json.annotationType
    if (json.annotationVisible) annotation.visible = json.annotationVisible
    if (json.annotationImageNames)
      annotation.imageNames = json.annotationImageNames
    if (json.navyAnnotation)
      annotation.navyAnnotation = KVNavyAnnotation.fromJSON(json.navyAnnotation)
    if (json.rooftopAnnotation)
      annotation.rooftopAnnotation = KVRooftopAnnotation.fromJSON(
        json.rooftopAnnotation
      )
    if (json.g1n6Annotation)
      annotation.g1n6Annotation = KVG1N6Annotation.fromJSON(json.g1n6Annotation)
    if (json.aerospaceAnnotation)
      annotation.aerospaceAnnotation = KVAerospaceAnnotation.fromJSON(
        json.aerospaceAnnotation
      )
    if (json.userName) annotation.userName = json.userName
    if (json.userEmail) annotation.userEmail = json.userEmail
    if (json.createdAt) annotation.createdAt = json.createdAt
    if (json.normal) {
      annotation.centeredNormal = {
        x: json.normal.x,
        y: json.normal.y,
        z: json.normal.z,
      }
    } else if (json.centeredNormal) {
      annotation.centeredNormal = {
        x: json.centeredNormal.x,
        y: json.centeredNormal.y,
        z: json.centeredNormal.z,
      }
    }

    if (json.centeredPosition) {
      annotation.centeredPosition = {
        x: json.centeredPosition.x,
        y: json.centeredPosition.y,
        z: json.centeredPosition.z,
      }
    }

    // Add segmentation flag parsing
    if (json.isSegmentationSurfaceArea !== undefined) {
      annotation.isSegmentationSurfaceArea = json.isSegmentationSurfaceArea
    }

    if (json.tags) {
      const tags: Record<string, KVTagMetadata> = {}
      for (const [key, value] of Object.entries(json.tags)) {
        tags[key] = value as KVTagMetadata
      }
      annotation.tags = tags
    }

    // Add group if present
    if (json.group) {
      annotation.group = json.group
    }

    // Add humanValidated flag
    if (json.humanValidated !== undefined) {
      annotation.humanValidated = json.humanValidated
    }

    // Add area and perimeter loading
    annotation.area = json.area ? parseFloat(json.area as string) : undefined
    annotation.perimeter = json.perimeter
      ? parseFloat(json.perimeter as string)
      : undefined

    // Add bounding box loading
    if (json.boundingBoxLength !== undefined) {
      annotation.boundingBoxLength = parseFloat(
        json.boundingBoxLength as string
      )
    }
    if (json.boundingBoxWidth !== undefined) {
      annotation.boundingBoxWidth = parseFloat(json.boundingBoxWidth as string)
    }

    // Handle geometry
    if (json.geometry) {
      if (json.geometry.kind === 'point') {
        annotation.geometry = new KVPointGeometry(
          json.geometry.position,
          json.geometry.normal
        )
      } else if (json.geometry.kind === 'polygon') {
        annotation.geometry = new KVPolygonGeometry(
          json.geometry.points,
          json.geometry.normal
        )
      }
    }

    return annotation
  }

  static toJSON(annotation: KVAnnotation): any {
    // Create base fields in json
    const json: any = {
      annotationId: annotation.id,
      annotationLabel: annotation.label,
      annotationNotes: annotation.notes,
      annotationSeverity: annotation.severity,
      annotationVisible: annotation.visible,
      annotationImageNames: annotation.imageNames,
      userName: annotation.userName,
      userEmail: annotation.userEmail,
      createdAt: annotation.createdAt,
      centeredNormal: annotation.centeredNormal,
      centeredPosition: annotation.centeredPosition,
      isSegmentationSurfaceArea: annotation.isSegmentationSurfaceArea ?? false,
      area: annotation.area,
      perimeter: annotation.perimeter,
      boundingBoxLength: annotation.boundingBoxLength,
      boundingBoxWidth: annotation.boundingBoxWidth,
      tags: annotation.tags,
      group: annotation.group,
      humanValidated: annotation.humanValidated,
    }
    // Add custom-optional forms
    if (annotation.navyAnnotation) {
      json.navyAnnotation = KVNavyAnnotation.toJSON(annotation.navyAnnotation)
    }
    if (annotation.rooftopAnnotation) {
      json.rooftopAnnotation = KVRooftopAnnotation.toJSON(
        annotation.rooftopAnnotation
      )
    }
    if (annotation.g1n6Annotation) {
      json.g1n6Annotation = KVG1N6Annotation.toJSON(annotation.g1n6Annotation)
    }
    if (annotation.aerospaceAnnotation) {
      json.aerospaceAnnotation = KVAerospaceAnnotation.toJSON(
        annotation.aerospaceAnnotation
      )
    }

    // Add geometry
    if (annotation.geometry) {
      json.geometry = annotation.geometry.toJSON()
    }
    return json
  }

  static toKestrelJSON(annotation: KVAnnotation): any {
    // Send Kestrel specific Payload base off of annotation type
    if (annotation.geometry?.kind === 'point') {
      return {
        annotationId: annotation.id,
        annotationLabel: annotation.label,
        annotationSeverity: annotation.severity,
        annotationVisible: annotation.visible,
        annotationPosition: annotation.geometry.position,
        annotationNormal: annotation.geometry.normal,
        annotationHistorical: annotation.historical,
        inspectionArea: annotation.tags?.inspectionArea?.value,
      }
    } else if (annotation.geometry?.kind === 'polygon') {
      return {
        surfaceAreaId: annotation.id,
        surfaceAreaLabel: annotation.label,
        surfaceAreaVisible: annotation.visible,
        surfaceAreaPoints: annotation.geometry.points,
        surfaceAreaSeverity: annotation.severity,
        isSegmentationSurfaceArea:
          annotation.isSegmentationSurfaceArea ?? false,
        area: annotation.area?.toString() || '',
        perimeter: annotation.perimeter?.toString() || '',
        boundingBoxLength: annotation.boundingBoxLength?.toString() || '',
        boundingBoxWidth: annotation.boundingBoxWidth?.toString() || '',
        surfaceAreaHistorical: annotation.historical,
        inspectionArea: annotation.tags?.inspectionArea?.value,
      }
    }
  }
  static kestrelPointAnnotations(annotations: KVAnnotation[]): any[] {
    return annotations
      .filter((annotation) => annotation.geometry?.kind === 'point')
      .map((annotation) => KVAnnotation.toKestrelJSON(annotation))
  }
  static kestrelPolygonAnnotations(annotations: KVAnnotation[]): any[] {
    return annotations
      .filter((annotation) => annotation.geometry?.kind === 'polygon')
      .map((annotation) => KVAnnotation.toKestrelJSON(annotation))
  }
}
